import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  getThreads,
  getThread,
  createThread,
  deleteThread,
  sendMessage,
  quickChat,
  type JamesThread,
  type JamesMessage,
  type StreamMessageOptions,
  type JamesToolUseStartEvent,
  type JamesToolUseResultEvent,
  type JamesAskUserEvent,
} from '../api/james';
import { type StreamingState } from '../context/JamesContext';
export type { StreamingState, ClarifyingQuestion } from '../context/JamesContext';

// Query hooks
export function useJamesThreads() {
  return useQuery({
    queryKey: ['james', 'threads'],
    queryFn: getThreads,
  });
}

export function useJamesThread(id: string | null) {
  return useQuery({
    queryKey: ['james', 'threads', id],
    queryFn: () => (id ? getThread(id) : Promise.resolve({ thread: null as unknown as JamesThread })),
    enabled: !!id,
  });
}

// Mutation hooks
export function useCreateJamesThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createThread,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
    },
  });
}

export function useDeleteJamesThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteThread,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
    },
  });
}

export interface UseJamesStreamResult {
  streamingState: StreamingState;
  streamingThreadId: string | null;
  sendMessage: (threadId: string, content: string) => Promise<void>;
  quickChat: (content: string) => Promise<string | null>; // Returns thread ID
  stop: () => void;
  reset: () => void;
}

export const initialStreamingState: StreamingState = {
  isStreaming: false,
  currentText: '',
  activeTools: new Map(),
  error: null,
  pendingUserMessage: null,
  clarifyingQuestion: null,
  wasInterrupted: false,
};

export function useJamesStream(): UseJamesStreamResult {
  const [streamingState, setStreamingState] = useState<StreamingState>(initialStreamingState);
  const [streamingThreadId, setStreamingThreadId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setStreamingState(initialStreamingState);
    setStreamingThreadId(null);
  }, []);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStreamingState((prev) => ({
      ...prev,
      isStreaming: false,
      wasInterrupted: true,
    }));
    queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
  }, [queryClient]);

  const handleStreamOptions = useCallback(
    (onThreadCreated?: (threadId: string) => void): StreamMessageOptions => ({
      onThreadCreated: (threadId) => {
        setStreamingThreadId(threadId);
        onThreadCreated?.(threadId);
      },
      onTextDelta: (text) => {
        setStreamingState((prev) => ({
          ...prev,
          currentText: prev.currentText + text,
        }));
      },
      onToolUseStart: (data: JamesToolUseStartEvent) => {
        setStreamingState((prev) => {
          const newTools = new Map(prev.activeTools);
          newTools.set(data.id, { name: data.name, status: 'running' });
          return { ...prev, activeTools: newTools };
        });
      },
      onToolUseResult: (data: JamesToolUseResultEvent) => {
        setStreamingState((prev) => {
          const newTools = new Map(prev.activeTools);
          const existingTool = newTools.get(data.id);
          if (existingTool) {
            const hasError = data.result && typeof data.result === 'object' && 'error' in data.result;
            newTools.set(data.id, {
              ...existingTool,
              status: hasError ? 'error' : 'completed',
              result: data.result,
            });
          }
          return { ...prev, activeTools: newTools };
        });
      },
      onAskUser: (data: JamesAskUserEvent) => {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          clarifyingQuestion: {
            id: data.id,
            question: data.question,
            options: data.options,
          },
        }));
        // Invalidate to save the assistant message with the ask_user tool call
        queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
      },
      onMessageComplete: () => {
        // Mark streaming as done but keep content visible until query updates
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error: null,
        }));
        // Invalidate thread queries to refresh with new messages
        queryClient.invalidateQueries({ queryKey: ['james', 'threads'] });
      },
      onError: (message) => {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error: message,
        }));
      },
    }),
    [queryClient]
  );

  const handleSendMessage = useCallback(
    async (threadId: string, content: string) => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setStreamingThreadId(threadId);

      setStreamingState({
        isStreaming: true,
        currentText: '',
        activeTools: new Map(),
        error: null,
        pendingUserMessage: content,
        clarifyingQuestion: null,
        wasInterrupted: false,
      });

      try {
        await sendMessage(threadId, content, handleStreamOptions(), controller.signal);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const error = err instanceof Error ? err.message : 'Failed to send message';
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error,
        }));
      }
    },
    [handleStreamOptions]
  );

  const handleQuickChat = useCallback(
    async (content: string): Promise<string | null> => {
      let threadId: string | null = null;
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setStreamingState({
        isStreaming: true,
        currentText: '',
        activeTools: new Map(),
        error: null,
        pendingUserMessage: content,
        clarifyingQuestion: null,
        wasInterrupted: false,
      });

      try {
        await quickChat(content, handleStreamOptions((id) => {
          threadId = id;
        }), controller.signal);
        return threadId;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return threadId;
        const error = err instanceof Error ? err.message : 'Failed to send message';
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          error,
        }));
        return null;
      }
    },
    [handleStreamOptions]
  );

  return {
    streamingState,
    streamingThreadId,
    sendMessage: handleSendMessage,
    quickChat: handleQuickChat,
    stop,
    reset,
  };
}

// Helper to construct messages for display, combining stored messages with streaming
export interface DisplayMessage extends JamesMessage {
  isStreaming?: boolean;
  wasInterrupted?: boolean;
  streamingTools?: Map<string, { name: string; status: 'running' | 'completed' | 'error'; result?: unknown }>;
}

export function useJamesMessages(
  threadMessages: JamesMessage[] | undefined,
  streamingState: StreamingState
): DisplayMessage[] {
  return useMemo(() => {
    const messages: DisplayMessage[] = [...(threadMessages ?? [])];

    // Show pending user message optimistically
    if (streamingState.pendingUserMessage) {
      // Check if this message is already in the stored messages — scan from end
      let messageAlreadySaved = false;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          messageAlreadySaved = messages[i].content === streamingState.pendingUserMessage;
          break;
        }
      }

      if (!messageAlreadySaved) {
        messages.push({
          id: 'pending-user',
          threadId: '',
          role: 'user',
          content: streamingState.pendingUserMessage,
          createdAt: new Date().toISOString(),
        });
      }
    }

    // Check if we need to show streaming content
    if (streamingState.currentText || streamingState.isStreaming) {
      // Find the last assistant message — scan from end
      let lastAssistantIndex = -1;
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant') {
          lastAssistantIndex = i;
          break;
        }
      }

      const contentAlreadySaved =
        lastAssistantIndex >= 0 && messages[lastAssistantIndex].content === streamingState.currentText;

      if (contentAlreadySaved) {
        // Content was saved by the server; carry over wasInterrupted to the stored message
        if (streamingState.wasInterrupted) {
          messages[lastAssistantIndex] = { ...messages[lastAssistantIndex], wasInterrupted: true };
        }
      } else {
        messages.push({
          id: 'streaming',
          threadId: '',
          role: 'assistant',
          content: streamingState.currentText,
          createdAt: new Date().toISOString(),
          isStreaming: streamingState.isStreaming,
          wasInterrupted: streamingState.wasInterrupted,
          streamingTools: streamingState.activeTools,
        });
      }
    }

    return messages;
  }, [threadMessages, streamingState.pendingUserMessage, streamingState.currentText, streamingState.isStreaming, streamingState.activeTools, streamingState.wasInterrupted]);
}
